import { FC, useMemo } from 'react';
import { useGetDegreesQuery, useGetCurrentUserQuery } from '@teachur/http-client';
import { DegreeGrid } from '../components/DegreeGrid/DegreeGrid';
import { Loader2 } from 'lucide-react';
import type { UserDegree } from '@teachur/http-client';
import type { DegreeWithEnrollment } from '../types';

export const DegreesListPage: FC = () => {
  const { data: currentUser } = useGetCurrentUserQuery();
  const { data: allDegrees = [], isLoading, error } = useGetDegreesQuery();

  const degrees = useMemo<DegreeWithEnrollment[]>(() => {
    if (!currentUser || !allDegrees.length) return [];

    const list = allDegrees as unknown as DegreeWithEnrollment[];

    if (currentUser.role === 'admin' || currentUser.role === 'super_admin') {
      return list;
    }

    const enrolledIds = new Set(
      (currentUser.enrolledDegrees ?? []).map((degree: UserDegree) => degree.degreeId)
    );

    return list
      .filter((degree: DegreeWithEnrollment) => degree.visibility === 'public')
      .map((degree: DegreeWithEnrollment) => ({
        ...degree,
        isEnrolled: enrolledIds.has(degree.id),
      }));
  }, [allDegrees, currentUser]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-100">
        <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-100">
        <p className="text-(--danger-D700)">Failed to load degrees. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 p-6 lg:p-8">
      <h1 className="[font-size:var(--fs-h5)]! lg:[font-size:var(--fs-h2)]! [font-family:var(--font-montserrat)]! font-bold! text-(--neutral-black-NB1000)! m-0!">
        Degrees
      </h1>

      <DegreeGrid degrees={degrees} emptyMessage="No degrees available at the moment..." />
    </div>
  );
};
